/**
 * SmartCameraSheet.js — шторка выбора помещения или категории поверх камеры.
 *
 * Два режима на одном экране: выбор (тап по чипу) и редактирование справочника
 * (долгое нажатие). Развели их специально: правка — редкое действие, и держать
 * крестики удаления рядом с кнопками выбора значит ловить случайные архивации
 * пальцем на ходу.
 *
 * Список перезапрашивается при каждом открытии: пока один воркер снимает,
 * второй мог переименовать комнату.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const LABEL_MAX = 40;

export default function SmartCameraSheet({
  kind,               // 'area' | 'cat' | любой другой — тогда titleKey/pickHintKey обязательны
  items, selectedId, canEdit,
  labelOf,
  editing, onToggleEdit,
  onPick, onAdd, onRename, onArchive, onDuplicate,
  onClose, busy,
  // Переопределение текста для новых потребителей шторки (например, Группы
  // фото «До/После»), не завязанных на area/cat. Не задано — старое поведение
  // по kind не меняется ни для одного существующего вызова.
  titleKey, pickHintKey, pickHintReadonlyKey,
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const addRef = useRef(null);
  const selected = items.find(i => i.id === selectedId);

  useEffect(() => { if (addOpen && addRef.current) addRef.current.focus(); }, [addOpen]);

  const submitAdd = async () => {
    const v = draft.trim();
    if (!v) { setAddOpen(false); return; }
    await onAdd(v);
    setDraft('');
    setAddOpen(false);
  };

  return (
    <div
      onPointerDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'absolute', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'flex-end',
      }}
    >
      <div style={{
        width: '100%', maxHeight: '78%', overflowY: 'auto',
        background: 'var(--bg2)', borderRadius: '20px 20px 0 0',
        padding: '14px 16px calc(26px + env(safe-area-inset-bottom, 0px))',
      }}>
        <h4 style={{ margin: '0 0 2px', fontSize: 16, color: 'var(--text)' }}>
          {t(titleKey || (kind === 'area' ? 'smartCamera.sheetAreaTitle' : 'smartCamera.sheetCatTitle'))}
        </h4>
        <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--text3)' }}>
          {editing
            ? t('smartCamera.sheetEditHint')
            : (canEdit
                ? t(pickHintKey || 'smartCamera.sheetPickHint')
                : t(pickHintReadonlyKey || 'smartCamera.sheetPickHintReadonly'))}
        </p>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {items.map((item) => (
            <SheetItem
              key={item.id}
              item={item}
              label={labelOf(item)}
              active={item.id === selectedId}
              editing={editing}
              busy={busy}
              onPick={() => onPick(item)}
              onRename={(v) => onRename(item.id, v)}
              onArchive={() => onArchive(item.id)}
            />
          ))}

          {/* Дублирование с инкрементом — только для помещений: спальня и ванная
              в квартире редко одни, а категория «Электрика 2» бессмысленна. */}
          {!editing && canEdit && kind === 'area' && selected && (
            <button
              type="button"
              onClick={() => onDuplicate(selected)}
              disabled={busy}
              data-ph-capture="smart_camera_area_duplicate"
              style={dashedBtn}
            >
              + {String(labelOf(selected) || '').replace(/\s+\d+$/, '')} 2
            </button>
          )}

          {editing && (addOpen ? (
            <span style={{ ...chipBase, borderStyle: 'dashed', borderColor: 'var(--accent, #6366f1)' }}>
              <input
                ref={addRef}
                value={draft}
                maxLength={LABEL_MAX}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submitAdd(); if (e.key === 'Escape') { setDraft(''); setAddOpen(false); } }}
                onBlur={submitAdd}
                placeholder={t('smartCamera.newItemPlaceholder')}
                style={inputStyle}
              />
            </span>
          ) : (
            <button type="button" onClick={() => setAddOpen(true)} disabled={busy}
              data-ph-capture="smart_camera_ref_add" style={dashedBtn}>
              + {t('smartCamera.addItem')}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          {canEdit ? (
            <button type="button" onClick={onToggleEdit} data-ph-capture="smart_camera_ref_edit_toggle"
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 13, fontWeight: 700, color: 'var(--accent, #6366f1)' }}>
              {editing ? t('smartCamera.backToPick') : t('smartCamera.editList')}
            </button>
          ) : <span />}
          <button type="button" onClick={onClose} style={primaryBtn}>{t('common.done')}</button>
        </div>
      </div>
    </div>
  );
}

/**
 * Один элемент справочника. В режиме правки ярлык редактируется на месте —
 * отдельная модалка переименования на телефоне лишний экран.
 *
 * ⚠️ Переименование меняет ярлык, но не ключ: на ключе держится сопоставление
 * с уже снятыми кадрами и с прошлыми отчётами этого объекта.
 */
function SheetItem({ item, label, active, editing, busy, onPick, onRename, onArchive }) {
  const { t } = useTranslation();
  const [value, setValue] = useState(label);
  useEffect(() => { setValue(label); }, [label]);

  if (!editing) {
    return (
      <button type="button" onClick={onPick} disabled={busy}
        data-ph-capture="smart_camera_ref_pick"
        style={{
          ...chipBase,
          borderColor: active ? 'var(--accent, #6366f1)' : 'var(--border)',
          background: active ? 'rgba(79,142,247,0.12)' : 'var(--bg2, #fff)',
          color: active ? 'var(--accent, #6366f1)' : 'var(--text)',
          cursor: busy ? 'default' : 'pointer',
        }}
      >
        {label}
      </button>
    );
  }

  return (
    <span style={{ ...chipBase, borderColor: active ? 'var(--accent, #6366f1)' : 'var(--border)' }}>
      <input
        value={value}
        maxLength={LABEL_MAX}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => { const v = value.trim(); if (v && v !== label) onRename(v); else setValue(label); }}
        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
        style={inputStyle}
      />
      {!item.is_protected && (
        <button type="button" onClick={onArchive} aria-label={t('common.delete')}
          data-ph-capture="smart_camera_ref_archive"
          style={{ background: 'none', border: 'none', color: '#ef4444', fontWeight: 800, cursor: 'pointer', padding: '0 2px', fontSize: 15 }}>
          ×
        </button>
      )}
    </span>
  );
}

const chipBase = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '10px 14px', borderRadius: 16, border: '1.5px solid var(--border)',
  background: 'var(--bg2, #fff)', fontSize: 14, fontWeight: 600, color: 'var(--text)',
};
const dashedBtn = {
  ...chipBase, borderStyle: 'dashed', color: 'var(--accent, #6366f1)', cursor: 'pointer',
};
const inputStyle = {
  border: 'none', outline: 'none', font: 'inherit', width: 120,
  background: 'transparent', color: 'inherit', padding: 0,
};
const primaryBtn = {
  padding: '9px 18px', borderRadius: 18, border: 'none',
  background: 'var(--accent, #6366f1)', color: '#fff', fontWeight: 750, fontSize: 14, cursor: 'pointer',
};
